/**
 * Soft≠Live Fleet Device Capabilities — Live GET /devices/{id}/capabilities.
 * Soft kind heuristics that painted fake verification stay deleted.
 */
import { useMemo } from "react";
import { html, Ico, Card, KV, Alert, go } from "./ui.js";
import { path, useApi } from "./api.js";
import { CAP_CATALOG } from "./edge-data.js";

/** @param {unknown} envelope GET /devices/{id}/capabilities */
export function deviceCapsViewOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const item = root.item && typeof root.item === "object" ? root.item : null;
  const catalog = Array.isArray(root.catalog)
    ? root.catalog.map((c) => String(c).trim()).filter(Boolean)
    : [];
  const claimed = item && Array.isArray(item.claimed)
    ? item.claimed.map((c) => String(c).trim()).filter(Boolean)
    : [];
  const verified = item && Array.isArray(item.verified)
    ? item.verified.map((c) => String(c).trim()).filter(Boolean)
    : [];
  return {
    deviceId: String(root.device_id || "").trim(),
    item,
    catalog,
    claimed,
    verified,
    claimedSet: new Set(claimed),
    verifiedSet: new Set(verified),
    deviceType: item ? String(item.device_type || "").trim() : "",
    persistence: String(root.persistence || (item && item.persistence) || "").trim(),
    honesty: String(root.honesty || "").trim(),
    source: item ? String(item.source || "").trim() : "",
    updatedAt: item ? String(item.updated_at || "").trim() : "",
  };
}

function sayFor(id) {
  const row = CAP_CATALOG.find((c) => c.id === id);
  return row ? row.say : id;
}

function clsFor(id) {
  const row = CAP_CATALOG.find((c) => c.id === id);
  return row ? row.cls : "live";
}

function statusOf(id, view) {
  if (view.verifiedSet.has(id)) return "verified";
  if (view.claimedSet.has(id)) return "claimed";
  return "empty";
}

function statusLabel(st) {
  if (st === "verified") return "Live verified";
  if (st === "claimed") return "claimed only";
  return "not claimed";
}

/**
 * Fleet Device → Capabilities desk.
 * @param {{ deviceId: string, toast: function }} props
 */
export function DeviceCapsLive({ deviceId, toast }) {
  const did = String(deviceId || "").trim();
  const live = useApi(did ? path("get__devices_p_capabilities", { pathParams: [did] }) : "");
  const view = useMemo(() => deviceCapsViewOf(live.data), [live.data]);
  const rows = useMemo(() => {
    const ids = view.catalog.length
      ? view.catalog
      : CAP_CATALOG.map((c) => c.id);
    return ids.map((id) => {
      const st = statusOf(id, view);
      return { id, say: sayFor(id), cls: clsFor(id), st, label: statusLabel(st) };
    });
  }, [view]);

  const reload = () => {
    live.reload && live.reload();
    toast && toast("Capabilities re-read · Live");
  };

  return html`<div className="cmc-caps-live cmc-caps-live--liquid" data-cmc-page="page99-device-live">
    <${Alert} tone="info" title="Live capability registry"
      say="Rows come from GET /devices/{id}/capabilities. Soft kind heuristics that painted fake verification stay deleted. Verified means claimed ∩ driver-tested on the Live registry. Primary CTAs carry · Live."/>

    ${live.loading && !live.data
      ? html`<p className="note">Reading Live registry…</p>`
      : null}

    ${live.error && !live.data
      ? html`<${Alert} tone=${/503|disabled/i.test(String(live.error)) ? "warn" : "bad"}
          title=${/503|disabled/i.test(String(live.error))
            ? "Capability registry off"
            : "Live capabilities unavailable"}
          say=${/503|disabled/i.test(String(live.error))
            ? "DEVICE_CAPABILITY_REGISTRY_ENABLED is off — nothing Soft invented. Arm the registry, then Re-read · Live."
            : String(live.error)}/>`
      : null}

    <${Card} title="Capabilities" meta=${view.item
        ? ("Live · " + (view.persistence || "pg"))
        : (live.error ? "unavailable" : "no registry row yet")}>
      <p className="sub">Claimed is what the operator registered. Verified is only what the registry
        kept after driver evidence. Empty stays empty.</p>

      <div className="btn-row cmc-caps-live__acts" style=${{ marginBottom:12, flexWrap:"wrap", gap:8 }}>
        <button className="btn btn--ghost btn--sm" type="button"
          disabled=${!did || live.loading}
          onClick=${reload}>
          <${Ico} d="refresh" w=${13}/>Re-read · Live</button>
        <button className="btn btn--ai btn--sm" type="button"
          onClick=${()=>go("/activate/capabilities")}>
          <${Ico} d="chip" w=${13}/>Register on Activate · Live</button>
        <button className="btn btn--go btn--sm" type="button"
          onClick=${()=>go("/edge/skills")}>
          <${Ico} d="grid" w=${13}/>Skills warehouse · function OTA</button>
      </div>

      <div className="grid g2" style=${{ gap:"0 16px", marginBottom:12 }}>
        <${KV} k="Device type" v=${view.deviceType || "—"} tone=${view.deviceType?"":"dim"}/>
        <${KV} k="Honesty" v=${view.honesty || (live.error ? "error" : "—")}/>
        <${KV} k="Claimed" v=${String(view.claimed.length)}/>
        <${KV} k="Verified" v=${String(view.verified.length)}
          tone=${view.verified.length ? "go" : "dim"}/>
        <${KV} k="Persistence" v=${view.persistence || "—"}/>
        <${KV} k="Updated" v=${view.updatedAt || "—"} tone="dim"/>
      </div>

      <div className="caps2">
        ${rows.map((r) => html`
          <div key=${r.id} className="cap2" data-st=${r.st} data-on=${r.st!=="empty"?"1":"0"}>
            <span className="cap2__d"/>
            <span className="cap2__t">
              <b>${r.id}</b>
              <code>${r.say}</code>
            </span>
            <span className="cap2__s" data-st=${r.st}>${r.label}</span>
          </div>`)}
      </div>

      ${!view.item && !live.error && !live.loading
        ? html`<p className="note" style=${{ marginTop:10 }}>
            No Live registry row for this device yet. Soft brochure capability grids stay deleted.
            Use Register on Activate to POST claimed ∩ tested seats.</p>`
        : null}
    </${Card}>
  </div>`;
}
